import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { v4 as uuidv4 } from 'uuid';
import Lottie from "lottie-react";
import dotsAnimation from "@carousel-office/shared-assets/dots.json";
import { AIPrimaryButton } from "../common/AIPrimaryButton";
import { ChatInput } from "./ChatInput";
import { ChatMessageItem } from "./ChatMessageItem";
import { FileUploadProgress } from "./FileUploadProgress";
import { FileConflictModal } from "./FileConflictModal";
import { ChatImageAttachments } from "./ChatImageAttachments";
import { useConversation } from "../../context/ConversationContext";
import { useSendMessage, notifyNewConversation } from "../../hooks/useSendMessage";
import { PlusIcon, StopIcon, SendIcon, UploadIcon, PromptLibraryIcon, StarIcon, ExternalLinkIcon } from "../../icons";
import TextInput from "../common/TextInput";
import { Colors } from "../../constants/colors";
import { getCurrentPromptHost, loadPrompts } from "../../services/promptStorage";
import { getConversationUIIndex } from "../../services/conversationUIStorage";
import { formatFileSize } from "../../utils/formatFileSize";
import { createImageAttachmentsFromFiles, deleteContextImageById, isImageFile, } from "./chatImageAttachmentHelpers";
import { MODEL_OPTIONS } from "../../constants/modelOptions";
import { isToolSemanticLogUiEnabled } from "./toolResultSemanticLog";
// Helper to format model name (first word bold)
const formatModelName = (modelId) => {
    // Look up the model in MODEL_OPTIONS to get the display text
    const modelOption = MODEL_OPTIONS.find(opt => opt.key === modelId);
    const displayText = modelOption?.text || modelId;
    // Split into first word (bold) and rest
    const words = displayText.split(' ');
    return {
        firstWord: words[0] || '',
        rest: words.slice(1).join(' '),
    };
};
// Helper to format reasoning mode
const formatReasoningMode = (mode) => {
    return mode.charAt(0).toUpperCase() + mode.slice(1);
};
const getSlideIndexingStatusText = (slideCountPill) => {
    const pluralize = (count, singular, plural) => count === 1 ? singular : plural;
    if (!slideCountPill) {
        return 'No slide indexing data';
    }
    const total = Math.max(0, slideCountPill.total || 0);
    const ready = Math.max(0, slideCountPill.ready || 0);
    const failed = Math.max(0, slideCountPill.failed || 0);
    const remaining = Math.max(total - ready - failed, 0);
    if (total === 0) {
        return 'No slides indexed yet';
    }
    if (remaining > 0) {
        return `Indexing ${remaining} ${pluralize(remaining, 'slide', 'slides')}...`;
    }
    if (failed > 0) {
        return `${ready}/${total} ${pluralize(total, 'slide', 'slides')} indexed · ${failed} ${pluralize(failed, 'failure', 'failures')}`;
    }
    return `All ${total} ${pluralize(total, 'slide', 'slides')} indexed.`;
};
const isSlideIndexingInProgress = (slideCountPill) => {
    if (!slideCountPill) {
        return false;
    }
    const total = Math.max(0, slideCountPill.total || 0);
    const ready = Math.max(0, slideCountPill.ready || 0);
    const failed = Math.max(0, slideCountPill.failed || 0);
    const remaining = Math.max(total - ready - failed, 0);
    return total > 0 && remaining > 0;
};
// Helper to format relative time for conversation history
const formatRelativeTime = (isoString) => {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1)
        return 'just now';
    if (diffMins < 60)
        return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24)
        return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7)
        return `${diffDays}d ago`;
    return date.toLocaleDateString();
};
const ISSUE_KEY_PATTERN = /(?:^|_)(error|errors|warning|warnings|issue|issues|problem|problems)$/i;
const IMAGE_KEY_PATTERN = /(?:^|_)(base64|image_base64|read_image_base64|chart_image_base64|image_data|image|thumbnail|preview)$/i;
const DATA_IMAGE_URL_PATTERN = /^data:image\/[a-z0-9.+-]+;base64,/i;
const BASE64_CHAR_PATTERN = /^[A-Za-z0-9+/=\r\n]+$/;
const isRecord = (value) => typeof value === "object" && value !== null && !Array.isArray(value);
const isLikelyBase64String = (value) => {
    if (value.length < 256 || value.includes(" ")) {
        return false;
    }
    return BASE64_CHAR_PATTERN.test(value);
};
const redactImagePayload = (value) => `[redacted image payload: ${value.length} chars]`;
const sanitizeForConversationExport = (value, stats, path = []) => {
    if (typeof value === "string") {
        if (DATA_IMAGE_URL_PATTERN.test(value) || isLikelyBase64String(value)) {
            stats.redactedImagePayloads += 1;
            return redactImagePayload(value);
        }
        return value;
    }
    if (value instanceof Date) {
        return value.toISOString();
    }
    if (Array.isArray(value)) {
        return value.map((item, index) => sanitizeForConversationExport(item, stats, [...path, `[${index}]`]));
    }
    if (!isRecord(value)) {
        return value;
    }
    const sanitized = {};
    for (const [key, nestedValue] of Object.entries(value)) {
        if (typeof nestedValue === "string") {
            const lowerKey = key.toLowerCase();
            if (IMAGE_KEY_PATTERN.test(lowerKey) ||
                (lowerKey === "url" && DATA_IMAGE_URL_PATTERN.test(nestedValue)) ||
                (lowerKey.includes("image") && isLikelyBase64String(nestedValue))) {
                stats.redactedImagePayloads += 1;
                sanitized[key] = redactImagePayload(nestedValue);
                continue;
            }
        }
        sanitized[key] = sanitizeForConversationExport(nestedValue, stats, [...path, key]);
    }
    return sanitized;
};
const collectSemanticLogs = (value, logs, path = [], depth = 0) => {
    if (depth > 8 || !value) {
        return;
    }
    if (Array.isArray(value)) {
        value.forEach((item, index) => collectSemanticLogs(item, logs, [...path, `[${index}]`], depth + 1));
        return;
    }
    if (!isRecord(value)) {
        return;
    }
    const semanticLog = value.semanticLog;
    const semanticLogText = value.semanticLogText;
    if (Array.isArray(semanticLog) || typeof semanticLogText === "string") {
        logs.push({
            path: path.length > 0 ? path.join(".") : "result",
            ...(Array.isArray(semanticLog) ? { semanticLog } : {}),
            ...(typeof semanticLogText === "string" ? { semanticLogText } : {}),
        });
    }
    for (const [key, nestedValue] of Object.entries(value)) {
        collectSemanticLogs(nestedValue, logs, [...path, key], depth + 1);
    }
};
const collectIssues = (value, issues, context, path = [], depth = 0) => {
    if (depth > 8 || value == null) {
        return;
    }
    if (Array.isArray(value)) {
        value.forEach((item, index) => collectIssues(item, issues, context, [...path, `[${index}]`], depth + 1));
        return;
    }
    if (!isRecord(value)) {
        return;
    }
    for (const [key, nestedValue] of Object.entries(value)) {
        const nextPath = [...path, key];
        if (nestedValue != null && ISSUE_KEY_PATTERN.test(key)) {
            const severity = /warning|warnings/i.test(key)
                ? "warning"
                : "error";
            issues.push({
                ...context,
                severity,
                path: nextPath.join("."),
                detail: nestedValue,
            });
        }
        collectIssues(nestedValue, issues, context, nextPath, depth + 1);
    }
};
const ChatPanel = ({ className, onOpenSettings, onOpenPromptLibraryDialog, conversations = [], onSelectConversation, onRefreshConversations, onRewriteConversationFromUserTurn, conversationsLoading = false, modelSettings, contextUsagePercent, slideCountPill, indexingEnabled = true, showSlideIndexingStatus = true, onPromptSelect, fetchMentionItems, }) => {
    // Detect planning mode by className prop
    const isPlanningMode = !!className;
    const showConversationExport = isToolSemanticLogUiEnabled();
    // Tooltip state for header buttons
    const [activeTooltip, setActiveTooltip] = useState(null);
    const [exportCopyStatus, setExportCopyStatus] = useState("idle");
    // Prompt library popup state
    const [showPromptPopup, setShowPromptPopup] = useState(false);
    const promptPopupRef = useRef(null);
    const promptButtonRef = useRef(null);
    const [promptPopupPosition, setPromptPopupPosition] = useState({ top: 0, arrowRight: 0 });
    // Conversation history popup state
    const [showHistoryPopup, setShowHistoryPopup] = useState(false);
    const historyPopupRef = useRef(null);
    const historyButtonRef = useRef(null);
    const [historyPopupPosition, setHistoryPopupPosition] = useState({ top: 0, arrowRight: 0 });
    const filteredConversations = useMemo(() => {
        const index = getConversationUIIndex();
        const storedIds = new Set(Object.keys(index));
        return conversations.filter((conv) => storedIds.has(conv.conversationId));
    }, [conversations]);
    // Load prompts from localStorage
    const [prompts, setPrompts] = useState([]);
    const currentPromptHost = getCurrentPromptHost();
    useEffect(() => {
        // Load prompts on mount
        const loadedPrompts = loadPrompts(currentPromptHost);
        setPrompts(loadedPrompts);
    }, [currentPromptHost]);
    useEffect(() => {
        if (exportCopyStatus === "idle") {
            return undefined;
        }
        const timeoutId = window.setTimeout(() => setExportCopyStatus("idle"), 1800);
        return () => window.clearTimeout(timeoutId);
    }, [exportCopyStatus]);
    // Refresh prompts from localStorage
    const refreshPrompts = useCallback(() => {
        const loadedPrompts = loadPrompts(currentPromptHost);
        setPrompts(loadedPrompts);
        console.log('[ChatPanel] Refreshed prompts from localStorage:', loadedPrompts.length);
    }, [currentPromptHost]);
    // Handler for adding message to prompt library
    const handleAddToPrompt = useCallback((content) => {
        if (!onOpenPromptLibraryDialog)
            return;
        // Store content in localStorage for dialog to pick up
        localStorage.setItem('carousel_pending_prompt_add', JSON.stringify({
            content,
            host: currentPromptHost,
            timestamp: Date.now()
        }));
        // Open the prompt library dialog in 'add' mode
        onOpenPromptLibraryDialog('add');
    }, [currentPromptHost, onOpenPromptLibraryDialog]);
    // Always use backend hooks
    const conversation = useConversation();
    const sendMessageHook = useSendMessage();
    const backendMessages = conversation.messages;
    const backendIsLoading = conversation.isLoading;
    const conversationId = conversation.conversationId;
    const backendError = conversation.error;
    const clearConversation = conversation.clearConversation;
    const setConversationId = conversation.setConversationId;
    const sendMessage = sendMessageHook.sendMessage;
    const stopExecution = sendMessageHook.stopExecution;
    const resetConversation = sendMessageHook.resetConversation;
    // Local state for UI
    const [inputValue, setInputValue] = useState("");
    const [isTextareaFocused, setIsTextareaFocused] = useState(false);
    const [isUserAtBottom, setIsUserAtBottom] = useState(true);
    const [uploadingFiles, setUploadingFiles] = useState([]);
    const [composerImages, setComposerImages] = useState([]);
    const [imageAttachmentErrors, setImageAttachmentErrors] = useState([]);
    const [isDragOverUploadZone, setIsDragOverUploadZone] = useState(false);
    const [pendingConflictSession, setPendingConflictSession] = useState(null);
    const [isCommittingConflicts, setIsCommittingConflicts] = useState(false);
    const messagesEndRef = useRef(null);
    const messagesContainerRef = useRef(null);
    const chatInputRef = useRef(null);
    const uploadInputRef = useRef(null);
    const mentionUploadIntentRef = useRef(false);
    const mentionUploadTargetRef = useRef(null);
    const isLoading = backendIsLoading;
    const hasContextUsage = contextUsagePercent != null && backendMessages.length > 0;
    const shouldShowIndexingStatus = showSlideIndexingStatus && Math.max(0, slideCountPill?.total || 0) > 1;
    const showHeaderMetaRow = hasContextUsage || shouldShowIndexingStatus;
    // Click outside handler for prompt popup
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (promptPopupRef.current &&
                !promptPopupRef.current.contains(e.target)) {
                setShowPromptPopup(false);
            }
        };
        if (showPromptPopup) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showPromptPopup]);
    // Click outside handler for history popup
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (historyPopupRef.current &&
                !historyPopupRef.current.contains(e.target)) {
                setShowHistoryPopup(false);
            }
        };
        if (showHistoryPopup) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showHistoryPopup]);
    // Calculate prompt popup position when it opens
    useEffect(() => {
        if (showPromptPopup && promptButtonRef.current) {
            const buttonRect = promptButtonRef.current.getBoundingClientRect();
            const buttonCenterX = buttonRect.left + buttonRect.width / 2;
            const taskpaneWidth = document.documentElement.clientWidth;
            // Arrow should point at button center
            // Popup has right: 8px, so popup right edge is at (taskpaneWidth - 8)
            // Arrow is 16px wide, center is 8px from right edge
            // paddingRight = (popupRightEdge) - buttonCenterX - 8
            //              = (taskpaneWidth - 8) - buttonCenterX - 8
            const arrowRight = taskpaneWidth - buttonCenterX - 16;
            setPromptPopupPosition({
                top: buttonRect.bottom + 8, // 8px gap below button
                arrowRight: Math.max(8, arrowRight), // minimum 8px from right edge
            });
        }
    }, [showPromptPopup]);
    // Calculate history popup position when it opens
    useEffect(() => {
        if (showHistoryPopup && historyButtonRef.current) {
            const buttonRect = historyButtonRef.current.getBoundingClientRect();
            const buttonCenterX = buttonRect.left + buttonRect.width / 2;
            const taskpaneWidth = document.documentElement.clientWidth;
            const arrowRight = taskpaneWidth - buttonCenterX - 16;
            setHistoryPopupPosition({
                top: buttonRect.bottom + 8,
                arrowRight: Math.max(8, arrowRight),
            });
        }
    }, [showHistoryPopup]);
    // Handle prompt selection from popup
    const handlePromptSelectFromPopup = (prompt) => {
        // Insert prompt content into chat input
        if (chatInputRef.current) {
            chatInputRef.current.insertText(prompt.content);
            chatInputRef.current.focus();
        }
        setShowPromptPopup(false);
        // Also call the callback if provided
        if (onPromptSelect) {
            onPromptSelect(prompt);
        }
    };
    const ACCEPTED_UPLOAD_FILE_TYPES = '.pdf,.xlsx,.xls,.csv,.doc,.docx,.ppt,.pptx,.pptm,.txt,.rtf';
    const acceptedUploadExtensions = useMemo(() => {
        return new Set(ACCEPTED_UPLOAD_FILE_TYPES.split(',')
            .map(item => item.trim().toLowerCase())
            .filter(Boolean));
    }, []);
    const filterSupportedUploadFiles = useCallback((files) => {
        return files.filter((file) => {
            const lowerName = file.name.toLowerCase();
            const dotIndex = lowerName.lastIndexOf('.');
            if (dotIndex < 0)
                return false;
            const extension = lowerName.slice(dotIndex);
            return acceptedUploadExtensions.has(extension);
        });
    }, [acceptedUploadExtensions]);
    const removeComposerImage = useCallback(async (imageId) => {
        const target = composerImages.find(image => image.id === imageId);
        setComposerImages(prev => prev.filter(image => image.id !== imageId));
        await deleteContextImageById(target?.contextId);
    }, [composerImages]);
    const addImagesFromFiles = useCallback(async (files) => {
        const { attachments, errors } = await createImageAttachmentsFromFiles(files, "composer_image");
        if (attachments.length > 0) {
            setComposerImages(prev => [...prev, ...attachments]);
        }
        if (errors.length > 0) {
            setImageAttachmentErrors(prev => [...errors, ...prev].slice(0, 5));
        }
    }, []);
    // Track filepath to ID mapping for WebSocket updates
    const filepathToIdMap = useRef(new Map());
    const progressUnsubscribe = useRef(null);
    // Set up RAG progress listener
    useEffect(() => {
        const setupProgressListener = async () => {
            if (!window.__onRagProgress) {
                return;
            }
            try {
                progressUnsubscribe.current = await window.__onRagProgress((progress) => {
                    // Find the file ID from filepath
                    const fileId = filepathToIdMap.current.get(progress.filepath);
                    if (!fileId) {
                        return;
                    }
                    // Map stage to status
                    let status;
                    switch (progress.stage) {
                        case 'complete':
                            status = 'done';
                            break;
                        case 'error':
                            status = 'error';
                            break;
                        case 'embedding':
                        case 'finalizing':
                        case 'batch':
                            status = 'processing';
                            break;
                        default:
                            status = 'uploading';
                    }
                    // Update the file progress
                    setUploadingFiles(prev => prev.map(f => f.id === fileId ? {
                        ...f,
                        progress: Math.round(progress.progress * 100),
                        status,
                        error: progress.error
                    } : f));
                    // Handle completion animation: 100% for 0.5s → hide % for 1s → remove
                    // Trigger when 'complete' is received OR when 'finalizing' hits 100%
                    const isComplete = progress.stage === 'complete' ||
                        (progress.stage === 'finalizing' && progress.progress >= 1);
                    if (isComplete) {
                        // After 0.5s showing 100%, change to "hiding" (no percentage shown)
                        setTimeout(() => {
                            setUploadingFiles(prev => prev.map(f => f.id === fileId ? { ...f, status: 'hiding' } : f));
                            // After 1s more, remove the file from the list
                            setTimeout(() => {
                                setUploadingFiles(prev => prev.filter(f => f.id !== fileId));
                                filepathToIdMap.current.delete(progress.filepath);
                            }, 1000);
                        }, 500);
                    }
                    else if (progress.stage === 'error') {
                        // Clean up mapping on error
                        filepathToIdMap.current.delete(progress.filepath);
                    }
                });
            }
            catch (error) {
                // Error setting up RAG progress listener
            }
        };
        setupProgressListener();
        return () => {
            if (progressUnsubscribe.current) {
                progressUnsubscribe.current();
                progressUnsubscribe.current = null;
            }
        };
    }, []);
    const appendUploadResultsToUi = (results) => {
        const timestamp = Date.now();
        const newEntries = results.map((result, index) => {
            const normalizedStatus = result.status === "processing" ? "uploading" :
                result.status === "error" ? "error" :
                    "done";
            return {
                id: `upload-${timestamp}-${index}`,
                name: result.filename,
                progress: normalizedStatus === "done" ? 100 : 0,
                size: result.file_size ? formatFileSize(result.file_size) : "Calculating...",
                status: normalizedStatus,
                error: normalizedStatus === "error" ? (result.message || "Upload failed") : undefined,
                filepath: result.filepath,
            };
        });
        newEntries.forEach((file) => {
            if (file.filepath && file.status !== "done" && file.status !== "error") {
                filepathToIdMap.current.set(file.filepath, file.id);
            }
        });
        setUploadingFiles(prev => [...prev, ...newEntries]);
        // Auto-hide skipped files after a short confirmation period.
        newEntries.forEach((entry) => {
            if (entry.status === "done") {
                setTimeout(() => {
                    setUploadingFiles(prev => prev.map(f => f.id === entry.id ? { ...f, status: "hiding" } : f));
                    setTimeout(() => {
                        setUploadingFiles(prev => prev.filter(f => f.id !== entry.id));
                        if (entry.filepath) {
                            filepathToIdMap.current.delete(entry.filepath);
                        }
                    }, 1000);
                }, 500);
            }
        });
        return newEntries;
    };
    const commitUploadSession = async (session, decisionsOverride) => {
        if (!window.__commitRagUploads) {
            throw new Error("RAG commit upload API is unavailable");
        }
        const overrideMap = new Map();
        (decisionsOverride || []).forEach((decision) => {
            overrideMap.set(decision.item_id, decision);
        });
        const decisions = session.files.map((item) => {
            const override = overrideMap.get(item.item_id);
            const action = (override?.action || item.default_action);
            return {
                item_id: item.item_id,
                action,
                replace_filepaths: override?.replace_filepaths
                    || (action === "replace" ? item.existing_matches.map((match) => match.filepath) : []),
            };
        });
        const response = await window.__commitRagUploads(session.upload_id, decisions);
        return appendUploadResultsToUi(response.files);
    };
    const startDirectUploadFlow = async (candidateFiles) => {
        const supportedFiles = filterSupportedUploadFiles(candidateFiles);
        if (supportedFiles.length === 0) {
            return { uploadEntries: [], pendingConflictResolution: false };
        }
        if (!window.__detectRagUploadConflicts || !window.__commitRagUploads) {
            throw new Error("Direct RAG upload APIs are unavailable");
        }
        const phase1 = await window.__detectRagUploadConflicts(supportedFiles);
        if (!phase1.files || phase1.files.length === 0) {
            return { uploadEntries: [], pendingConflictResolution: false };
        }
        if (phase1.conflicts_count > 0) {
            setPendingConflictSession(phase1);
            return { uploadEntries: [], pendingConflictResolution: true };
        }
        const uploadEntries = await commitUploadSession(phase1);
        return { uploadEntries, pendingConflictResolution: false };
    };
    // Handle upload button click.
    const handleUploadFileClick = (origin = "composer", mentionTarget) => {
        mentionUploadIntentRef.current = origin === "mention";
        mentionUploadTargetRef.current =
            origin === "mention" ? (mentionTarget || chatInputRef.current) : null;
        uploadInputRef.current?.click();
    };
    const handleMentionUploadRequest = (target) => {
        // Null target means "clear mention upload routing", not "open picker".
        if (!target) {
            mentionUploadIntentRef.current = false;
            mentionUploadTargetRef.current = null;
            return;
        }
        handleUploadFileClick("mention", target);
    };
    const handleFileInputChange = async (event) => {
        const selectedFiles = Array.from(event.target.files || []);
        event.target.value = "";
        if (selectedFiles.length === 0) {
            mentionUploadIntentRef.current = false;
            mentionUploadTargetRef.current = null;
            return;
        }
        try {
            const uploadOriginIsMention = mentionUploadIntentRef.current;
            const { uploadEntries, pendingConflictResolution } = await startDirectUploadFlow(selectedFiles);
            if (uploadOriginIsMention && uploadEntries.length > 0) {
                (mentionUploadTargetRef.current || chatInputRef.current)?.insertUploadedMentions(uploadEntries);
            }
            if (!pendingConflictResolution) {
                mentionUploadIntentRef.current = false;
                mentionUploadTargetRef.current = null;
            }
        }
        catch (error) {
            mentionUploadIntentRef.current = false;
            mentionUploadTargetRef.current = null;
            // Swallow and leave upload UI untouched on selection errors.
        }
    };
    const handleUploadZoneDragEnter = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (event.dataTransfer?.types.includes("Files")) {
            setIsDragOverUploadZone(true);
        }
    };
    const handleUploadZoneDragOver = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (event.dataTransfer) {
            event.dataTransfer.dropEffect = "copy";
        }
        setIsDragOverUploadZone(true);
    };
    const handleUploadZoneDragLeave = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (!event.currentTarget.contains(event.relatedTarget)) {
            setIsDragOverUploadZone(false);
        }
    };
    const handleUploadZoneDrop = async (event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragOverUploadZone(false);
        const droppedFiles = Array.from(event.dataTransfer?.files || []);
        if (droppedFiles.length === 0) {
            return;
        }
        try {
            const droppedImageFiles = droppedFiles.filter(isImageFile);
            const droppedDocumentFiles = droppedFiles.filter(file => !isImageFile(file));
            if (droppedImageFiles.length > 0) {
                await addImagesFromFiles(droppedImageFiles);
            }
            if (droppedDocumentFiles.length > 0) {
                await startDirectUploadFlow(droppedDocumentFiles);
            }
        }
        catch (error) {
            // Ignore failed drops to keep chat input responsive.
        }
    };
    const handleComposerPasteCapture = async (event) => {
        const clipboardItems = Array.from(event.clipboardData?.items || []);
        const pastedImageFiles = clipboardItems
            .filter(item => item.kind === "file")
            .map(item => item.getAsFile())
            .filter((file) => !!file && isImageFile(file));
        if (pastedImageFiles.length === 0) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        await addImagesFromFiles(pastedImageFiles);
    };
    // Handle removing/cancelling a file upload
    const handleRemoveUploadingFile = async (fileId) => {
        const file = uploadingFiles.find(f => f.id === fileId);
        // If file is still uploading/processing, cancel on the backend
        if (file && file.filepath && (file.status === 'uploading' || file.status === 'processing')) {
            if (window.__cancelRagUploads) {
                try {
                    await window.__cancelRagUploads([file.filepath]);
                }
                catch (error) {
                    // Error cancelling upload
                }
            }
            // Clean up filepath mapping
            if (file.filepath) {
                filepathToIdMap.current.delete(file.filepath);
            }
        }
        setUploadingFiles(prev => prev.filter(f => f.id !== fileId));
    };
    // Convert messages to display format
    const displayMessages = useMemo(() => {
        const sourceMessages = backendMessages;
        const converted = [];
        const isLastMessage = (index) => index === sourceMessages.length - 1;
        sourceMessages.forEach((msg, index) => {
            const stableTimestamp = msg.timestamp?.getTime() || index * 1000000;
            if (msg.role === "user") {
                const basicMsg = {
                    id: msg.id || `msg-${index}-${stableTimestamp}`,
                    text: msg.content,
                    sender: "user",
                    userImageAttachments: msg.userImageAttachments,
                    mentions: msg.mentions,
                    html: msg.html,
                    timestamp: msg.timestamp || new Date(),
                    // Support step message fields from planning mode
                    isStepMessage: msg.isStepMessage,
                    stepLabel: msg.stepLabel,
                    stepValue: msg.stepValue,
                };
                converted.push(basicMsg);
            }
            else if (msg.role === "assistant") {
                const isStreaming = isLoading && isLastMessage(index);
                const basicMsg = {
                    id: msg.id || `assistant-${index}-${stableTimestamp}`,
                    sender: "assistant",
                    timestamp: msg.timestamp || new Date(),
                    isStreaming,
                    components: msg.components || [],
                    // Support tool calls from planning mode
                    toolCalls: msg.toolCalls,
                    // Support text content for planning mode
                    text: msg.content,
                };
                converted.push(basicMsg);
            }
            else if (msg.role === "tool" && msg.toolCall) {
                converted.push(msg);
            }
        });
        return converted;
    }, [backendMessages, isLoading]);
    const buildConversationExportPayload = useCallback(() => {
        const sanitizeStats = {
            redactedImagePayloads: 0,
        };
        const sanitizedMessages = backendMessages.map((message) => sanitizeForConversationExport(message, sanitizeStats));
        const toolRecords = [];
        const issues = [];
        if (backendError) {
            issues.push({
                severity: "error",
                source: "conversation",
                messageIndex: -1,
                messageId: null,
                path: "conversation.error",
                detail: backendError,
            });
        }
        backendMessages.forEach((message, messageIndex) => {
            const messageId = message.id || null;
            if (Array.isArray(message.components)) {
                message.components.forEach((component, componentIndex) => {
                    if (component.type === "error") {
                        issues.push({
                            severity: "error",
                            source: "conversation",
                            messageIndex,
                            messageId,
                            path: `components[${componentIndex}]`,
                            detail: component.content,
                        });
                    }
                });
            }
            if (message.role !== "tool" || !message.toolCall) {
                return;
            }
            const sanitizedInput = sanitizeForConversationExport(message.toolCall.params, sanitizeStats);
            const sanitizedOutput = sanitizeForConversationExport(message.toolCall.result, sanitizeStats);
            const semanticLogs = [];
            collectSemanticLogs(sanitizedOutput, semanticLogs);
            const toolName = message.toolCall.name || "unknown_tool";
            const toolRecord = {
                messageIndex,
                messageId,
                toolCallId: message.toolCall.id || null,
                toolName,
                status: message.toolCall.status || null,
                success: typeof message.toolCall.success === "boolean" ? message.toolCall.success : null,
                input: sanitizedInput,
                output: sanitizedOutput,
                semanticLogs,
            };
            toolRecords.push(toolRecord);
            if (toolRecord.status === "error" || toolRecord.success === false) {
                issues.push({
                    severity: "error",
                    source: "tool",
                    messageIndex,
                    messageId,
                    toolName,
                    path: "tool.status",
                    detail: {
                        status: toolRecord.status,
                        success: toolRecord.success,
                    },
                });
            }
            collectIssues(sanitizedOutput, issues, {
                source: "tool",
                messageIndex,
                messageId,
                toolName,
            }, ["toolOutput"]);
        });
        const executionLogs = toolRecords
            .flatMap((record) => record.semanticLogs.map((entry) => ({
            ...entry,
            path: `${record.toolName}.${entry.path}`,
        })));
        return {
            exportedAt: new Date().toISOString(),
            conversationId,
            messageCount: backendMessages.length,
            sanitizeStats,
            messages: sanitizedMessages,
            tools: toolRecords,
            executionLogs,
            issues,
        };
    }, [backendError, backendMessages, conversationId]);
    const handleExportConversation = useCallback(async () => {
        try {
            const payload = buildConversationExportPayload();
            const jsonText = JSON.stringify(payload, null, 2);
            if (!navigator.clipboard || typeof navigator.clipboard.writeText !== "function") {
                throw new Error("Clipboard API unavailable");
            }
            await navigator.clipboard.writeText(jsonText);
            setExportCopyStatus("copied");
            setActiveTooltip("export-conversation");
        }
        catch (error) {
            console.error("[ChatPanel] Failed to export conversation:", error);
            setExportCopyStatus("failed");
            setActiveTooltip("export-conversation");
        }
    }, [buildConversationExportPayload]);
    const buildMessageWithMentionReminders = useCallback((text, mentions) => {
        if (mentions.length === 0) {
            return text;
        }
        const fileReminders = mentions
            .map((mention) => {
            if (mention.source === "ppt" || mention.category === "presentation") {
                return `The user tagged "${mention.label}" (a PowerPoint presentation). You can read its slides using the readSlide tool or search it using the PresentationSearchAgent.`;
            }
            if (mention.source === "workbook" ||
                mention.category === "cellRange" ||
                mention.category === "chart" ||
                mention.category === "sheet") {
                return `The user tagged "${mention.label}" (Excel workbook context). Use the Excel tools (readContent/readCharts/listSheets/readExcel) to inspect and operate on this workbook context.`;
            }
            return `The user tagged "${mention.label}" (an external file). You can search it using RAG/ExternalFileSearchAgent, or if it's an Excel file, use the readExcel tool.`;
        })
            .join("\n");
        return `${text}\n\n<non_user_automated_reminder>\n${fileReminders}\n</non_user_automated_reminder>`;
    }, []);
    // Handle send message using backend hooks
    const handleSendMessage = async () => {
        if ((inputValue.trim() === "" && composerImages.length === 0) || isLoading)
            return;
        if (!conversationId) {
            return;
        }
        const contentWithMentions = chatInputRef.current?.getContentWithMentions() || {
            text: inputValue,
            mentions: [],
            html: ''
        };
        let messageToSend = buildMessageWithMentionReminders(contentWithMentions.text, contentWithMentions.mentions);
        const imageContextIds = composerImages
            .map(image => image.contextId)
            .filter((id) => !!id);
        const userImageAttachments = composerImages.map((image) => ({
            contextId: image.contextId,
            name: image.name,
            mimeType: image.mimeType,
            originalMimeType: image.originalMimeType,
            converted: image.converted,
            sizeBytes: image.sizeBytes,
        }));
        if (!messageToSend.trim() && imageContextIds.length > 0) {
            messageToSend = `Attached ${imageContextIds.length} image${imageContextIds.length === 1 ? "" : "s"} for context.`;
        }
        setInputValue("");
        chatInputRef.current?.clear();
        setIsUserAtBottom(true);
        setComposerImages([]);
        setImageAttachmentErrors([]);
        const sendOptions = imageContextIds.length > 0 ||
            userImageAttachments.length > 0 ||
            contentWithMentions.mentions.length > 0 ||
            !!contentWithMentions.html
            ? {
                ...(imageContextIds.length > 0
                    ? {
                        context: [{
                                context: imageContextIds,
                                purpose: `User-attached image context (${imageContextIds.length} image${imageContextIds.length === 1 ? "" : "s"})`,
                            }],
                    }
                    : {}),
                ...(userImageAttachments.length > 0
                    ? { userImageAttachments }
                    : {}),
                ...(contentWithMentions.mentions.length > 0
                    ? { mentions: contentWithMentions.mentions }
                    : {}),
                ...(contentWithMentions.html
                    ? { html: contentWithMentions.html }
                    : {}),
            }
            : undefined;
        await sendMessage(messageToSend, sendOptions);
    };
    const handleEditFromUserTurn = useCallback(async (userTurnOrdinal, editedText, editedAttachments, editedMentions, editedHtml) => {
        if (!onRewriteConversationFromUserTurn || isLoading || !conversationId) {
            return;
        }
        await onRewriteConversationFromUserTurn(userTurnOrdinal);
        const imageContextIds = editedAttachments
            .map((attachment) => attachment.contextId)
            .filter((id) => !!id);
        let messageToSend = buildMessageWithMentionReminders(editedText, editedMentions);
        if (!messageToSend.trim() && imageContextIds.length > 0) {
            messageToSend = `Attached ${imageContextIds.length} image${imageContextIds.length === 1 ? "" : "s"} for context.`;
        }
        const sendOptions = imageContextIds.length > 0 ||
            editedAttachments.length > 0 ||
            editedMentions.length > 0 ||
            !!editedHtml
            ? {
                ...(imageContextIds.length > 0
                    ? {
                        context: [{
                                context: imageContextIds,
                                purpose: `User-attached image context (${imageContextIds.length} image${imageContextIds.length === 1 ? "" : "s"})`,
                            }],
                    }
                    : {}),
                ...(editedAttachments.length > 0
                    ? { userImageAttachments: editedAttachments }
                    : {}),
                ...(editedMentions.length > 0
                    ? { mentions: editedMentions }
                    : {}),
                ...(editedHtml
                    ? { html: editedHtml }
                    : {}),
            }
            : undefined;
        setIsUserAtBottom(true);
        await sendMessage(messageToSend, sendOptions);
    }, [
        buildMessageWithMentionReminders,
        conversationId,
        isLoading,
        onRewriteConversationFromUserTurn,
        sendMessage,
    ]);
    const handleKeyDown = (event) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            handleSendMessage();
        }
    };
    const handleNewConversation = async () => {
        // Stop any ongoing execution
        if (isLoading) {
            stopExecution();
        }
        resetConversation();
        clearConversation();
        mentionUploadIntentRef.current = false;
        mentionUploadTargetRef.current = null;
        // Cleanup unsent composer images when starting a fresh conversation.
        if (composerImages.length > 0) {
            for (const image of composerImages) {
                await deleteContextImageById(image.contextId);
            }
        }
        setComposerImages([]);
        setImageAttachmentErrors([]);
        const newConversationId = uuidv4();
        setConversationId(newConversationId);
        // Notify the message service to register this conversation with backend services
        try {
            await notifyNewConversation(newConversationId);
        }
        catch (err) {
            // Failed to register new conversation
        }
        // Clear input
        setInputValue("");
        if (chatInputRef.current) {
            chatInputRef.current.clear();
        }
    };
    // Check if user is scrolled to bottom
    const checkIfAtBottom = useCallback(() => {
        if (!messagesContainerRef.current)
            return true;
        const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
        const threshold = 100;
        const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
        return distanceFromBottom < threshold;
    }, []);
    // Handle scroll events to track user position
    const handleScroll = useCallback(() => {
        const atBottom = checkIfAtBottom();
        setIsUserAtBottom(atBottom);
    }, [checkIfAtBottom]);
    // Auto-scroll to bottom when messages change
    useEffect(() => {
        if (isUserAtBottom && messagesContainerRef.current) {
            // Use scrollTop instead of scrollIntoView to avoid scrolling parent containers
            const container = messagesContainerRef.current;
            container.scrollTo({
                top: container.scrollHeight,
                behavior: 'smooth',
            });
        }
    }, [displayMessages, isLoading, isUserAtBottom]);
    // Add scroll listener (same for both modes)
    useEffect(() => {
        const container = messagesContainerRef.current;
        if (!container)
            return undefined;
        container.addEventListener('scroll', handleScroll);
        return () => container.removeEventListener('scroll', handleScroll);
    }, [handleScroll]);
    // Keyboard shortcut: Ctrl+X to stop execution
    useEffect(() => {
        const handleKeyDown = (event) => {
            if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'x') {
                if (isLoading && document.activeElement?.tagName !== 'INPUT' && document.activeElement?.tagName !== 'TEXTAREA') {
                    event.preventDefault();
                    stopExecution();
                }
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isLoading, stopExecution]);
    // Unified layout with planning mode support
    return (_jsxs("div", { className: isPlanningMode ? className : "flex flex-col h-screen bg-white", children: [!isPlanningMode && (_jsxs("div", { className: "px-3 py-[6px] flex flex-col gap-[4px]", style: {
                    background: 'linear-gradient(180deg, rgba(255,255,255,0.98) 75%, rgba(255,255,255,0) 100%)',
                }, children: [_jsxs("div", { className: "flex items-center justify-between gap-2", children: [_jsx("div", { className: "flex items-center gap-3 flex-1", children: _jsxs("button", { onClick: () => onOpenSettings?.('carousel-chat'), className: "group inline-flex items-center gap-1 px-1 py-[1.5px] rounded-md border transition-colors cursor-pointer whitespace-nowrap shrink-0", style: {
                                        backgroundColor: Colors.Gray100,
                                        borderColor: Colors.Gray200,
                                    }, onMouseEnter: (e) => {
                                        e.currentTarget.style.backgroundColor = Colors.Gray200;
                                        e.currentTarget.style.borderColor = Colors.Gray300;
                                    }, onMouseLeave: (e) => {
                                        e.currentTarget.style.backgroundColor = Colors.Gray100;
                                        e.currentTarget.style.borderColor = Colors.Gray200;
                                    }, "aria-label": "Open model settings", children: [modelSettings?.reasoningMode &&
                                            modelSettings.reasoningMode !== 'off' &&
                                            modelSettings.reasoningMode.toLowerCase() !== 'off' && (_jsxs("div", { className: "inline-flex items-center gap-1", children: [_jsx(StarIcon, { size: 15, className: "shrink-0" }), _jsx("span", { className: "text-[13px] font-bold leading-[150%]", style: { color: Colors.PrimaryPurple60 }, children: formatReasoningMode(modelSettings.reasoningMode) })] })), _jsxs("div", { className: "inline-flex items-center gap-1", children: [modelSettings?.chatModel ? (_jsxs(_Fragment, { children: [_jsx("span", { className: "text-[13px] font-bold leading-[150%]", style: { color: Colors.Gray700 }, children: formatModelName(modelSettings.chatModel).firstWord }), _jsx("span", { className: "text-[13px] font-normal leading-[150%]", style: { color: Colors.Gray500 }, children: formatModelName(modelSettings.chatModel).rest })] })) : (_jsx("span", { className: "text-[13px] font-normal leading-[150%]", style: { color: Colors.Gray500 }, children: "Select Model" })), _jsx(ExternalLinkIcon, { size: 12, color: Colors.Gray500, className: "ml-0.5" })] })] }) }), _jsxs("nav", { className: "inline-flex items-center gap-1 mr-2", "aria-label": "Main navigation", children: [_jsxs("div", { className: "relative", children: [_jsx("button", { className: "flex w-8 h-8 items-center justify-center rounded-md transition-colors cursor-pointer", style: {
                                                    backgroundColor: activeTooltip === 'new-chat' ? Colors.PrimaryPurple10 : 'transparent',
                                                }, "aria-label": "New Conversation", onClick: handleNewConversation, onMouseEnter: () => setActiveTooltip('new-chat'), onMouseLeave: () => setActiveTooltip(null), children: _jsx(PlusIcon, { size: 16, color: Colors.PrimaryPurple60 }) }), activeTooltip === 'new-chat' && (_jsx("div", { className: "absolute top-full left-1/2 -translate-x-1/2 mt-2 z-50", children: _jsxs("div", { className: "relative", children: [_jsx("svg", { className: "absolute -top-2 left-1/2 -translate-x-1/2", width: "16", height: "8", viewBox: "0 0 16 8", fill: "none", children: _jsx("path", { d: "M8 0L16 8H0L8 0Z", fill: Colors.Gray800 }) }), _jsx("div", { className: "px-2 py-0.5 rounded-lg whitespace-nowrap text-[13px] text-white", style: { backgroundColor: Colors.Gray800 }, children: "New Chat" })] }) }))] }), _jsxs("div", { className: "relative", ref: promptPopupRef, children: [_jsx("button", { ref: promptButtonRef, className: "flex w-8 h-8 items-center justify-center rounded-md transition-colors cursor-pointer", style: {
                                                    backgroundColor: (showPromptPopup || activeTooltip === 'prompt-library') ? Colors.PrimaryPurple10 : 'transparent',
                                                }, "aria-label": "Prompt Library", onClick: () => {
                                                    // Refresh prompts from localStorage before opening
                                                    if (!showPromptPopup) {
                                                        refreshPrompts();
                                                    }
                                                    setShowPromptPopup(!showPromptPopup);
                                                }, onMouseEnter: () => !showPromptPopup && setActiveTooltip('prompt-library'), onMouseLeave: () => setActiveTooltip(null), children: _jsx(PromptLibraryIcon, { size: 16, color: Colors.PrimaryPurple60 }) }), activeTooltip === 'prompt-library' && !showPromptPopup && (_jsx("div", { className: "absolute top-full left-1/2 -translate-x-1/2 mt-2 z-50", children: _jsxs("div", { className: "relative", children: [_jsx("svg", { className: "absolute -top-2 left-1/2 -translate-x-1/2", width: "16", height: "8", viewBox: "0 0 16 8", fill: "none", children: _jsx("path", { d: "M8 0L16 8H0L8 0Z", fill: Colors.Gray800 }) }), _jsx("div", { className: "px-2 py-0.5 rounded-lg whitespace-nowrap text-[13px] text-white", style: { backgroundColor: Colors.Gray800 }, children: "Prompt Library" })] }) })), showPromptPopup && (_jsxs("div", { className: "fixed z-50", style: {
                                                    top: `${promptPopupPosition.top}px`,
                                                    left: '8px',
                                                    right: '8px',
                                                }, children: [_jsx("div", { className: "flex justify-end", style: { paddingRight: `${promptPopupPosition.arrowRight}px` }, children: _jsxs("svg", { width: "16", height: "8", viewBox: "0 0 16 8", fill: "none", children: [_jsx("path", { d: "M8 0L16 8H0L8 0Z", fill: "white" }), _jsx("path", { d: "M8 1L14.5 7H1.5L8 1Z", fill: "white" }), _jsx("path", { d: "M0 8L8 0L16 8", stroke: Colors.Gray300, strokeWidth: "1", fill: "none" })] }) }), _jsxs("div", { className: "flex flex-col rounded-lg overflow-hidden border -mt-px", style: {
                                                            backgroundColor: 'white',
                                                            borderColor: Colors.Gray300,
                                                            boxShadow: '0px 0px 8px -4px rgba(0,0,0,0.1), 0px 6px 6px 0px rgba(0,0,0,0.06), 0px 2px 6px 0px rgba(0,0,0,0.04), 0px 0px 1px 0px rgba(0,0,0,0.04)',
                                                        }, children: [_jsxs("div", { className: "flex items-center gap-2 px-3 py-2", children: [_jsx("h3", { className: "flex-1 font-bold leading-[1.2] text-[15px]", style: { color: 'black' }, children: "Prompt Library" }), _jsx("button", { className: "font-bold leading-normal text-[13px] cursor-pointer hover:opacity-80 transition-opacity bg-transparent border-none", style: { color: Colors.PrimaryPurple70 }, onClick: () => {
                                                                            setShowPromptPopup(false);
                                                                            onOpenPromptLibraryDialog?.();
                                                                        }, children: "Settings" })] }), prompts.length === 0 ? (
                                                            /* Empty state */
                                                            _jsx("div", { className: "flex items-center justify-center py-6", children: _jsx("p", { className: "font-sans font-normal leading-normal text-[13px] text-center", style: { color: Colors.Gray990 }, children: "No prompts found" }) })) : (_jsxs(_Fragment, { children: [_jsx("p", { className: "px-3 pb-2 font-sans font-normal leading-normal text-[13px]", style: { color: Colors.Gray600 }, children: "Click a prompt to add it to the chat" }), _jsx("div", { className: "h-px w-full", style: { backgroundColor: Colors.Gray300 } }), _jsxs("p", { className: "px-3 py-1.5 font-sans font-normal leading-normal text-[13px]", style: { color: Colors.Gray600 }, children: [prompts.length, " prompt", prompts.length !== 1 ? 's' : ''] }), _jsx("div", { className: "flex flex-col gap-2 px-3 pb-3 max-h-[350px] overflow-y-auto", children: prompts.map((prompt) => (_jsxs("button", { className: "flex flex-col items-start gap-1 p-2 rounded-lg cursor-pointer text-left transition-all border border-transparent", style: {
                                                                                backgroundColor: Colors.Gray100,
                                                                            }, onMouseEnter: (e) => {
                                                                                e.currentTarget.style.backgroundColor = Colors.Gray200;
                                                                                e.currentTarget.style.borderColor = Colors.Gray300;
                                                                            }, onMouseLeave: (e) => {
                                                                                e.currentTarget.style.backgroundColor = Colors.Gray100;
                                                                                e.currentTarget.style.borderColor = 'transparent';
                                                                            }, onClick: () => handlePromptSelectFromPopup(prompt), children: [_jsx("p", { className: "font-sans font-bold leading-normal text-[13px] line-clamp-1 w-full", style: { color: Colors.Gray990 }, children: prompt.title }), _jsx("p", { className: "font-sans font-normal leading-normal text-[13px] line-clamp-3 w-full", style: { color: Colors.Gray800 }, children: prompt.content })] }, prompt.id))) })] }))] })] }))] }), _jsxs("div", { className: "relative", ref: historyPopupRef, children: [_jsx("button", { ref: historyButtonRef, className: "flex w-8 h-8 items-center justify-center rounded-md transition-colors cursor-pointer", style: {
                                                    backgroundColor: (showHistoryPopup || activeTooltip === 'history') ? Colors.PrimaryPurple10 : 'transparent',
                                                }, "aria-label": "Conversation History", onClick: () => {
                                                    // Refresh conversations before opening
                                                    if (!showHistoryPopup && onRefreshConversations) {
                                                        onRefreshConversations();
                                                    }
                                                    setShowHistoryPopup(!showHistoryPopup);
                                                }, onMouseEnter: () => !showHistoryPopup && setActiveTooltip('history'), onMouseLeave: () => setActiveTooltip(null), children: _jsx("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", children: _jsx("path", { d: "M12 8V12L15 15M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z", stroke: Colors.PrimaryPurple60, strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" }) }) }), activeTooltip === 'history' && !showHistoryPopup && (_jsx("div", { className: "absolute top-full left-1/2 -translate-x-1/2 mt-2 z-50", children: _jsxs("div", { className: "relative", children: [_jsx("svg", { className: "absolute -top-2 left-1/2 -translate-x-1/2", width: "16", height: "8", viewBox: "0 0 16 8", fill: "none", children: _jsx("path", { d: "M8 0L16 8H0L8 0Z", fill: Colors.Gray800 }) }), _jsx("div", { className: "px-2 py-0.5 rounded-lg whitespace-nowrap text-[13px] text-white", style: { backgroundColor: Colors.Gray800 }, children: "History" })] }) })), showHistoryPopup && (_jsxs("div", { className: "fixed z-50", style: {
                                                    top: `${historyPopupPosition.top}px`,
                                                    left: '8px',
                                                    right: '8px',
                                                }, children: [_jsx("div", { className: "flex justify-end", style: { paddingRight: `${historyPopupPosition.arrowRight}px` }, children: _jsxs("svg", { width: "16", height: "8", viewBox: "0 0 16 8", fill: "none", children: [_jsx("path", { d: "M8 0L16 8H0L8 0Z", fill: "white" }), _jsx("path", { d: "M8 1L14.5 7H1.5L8 1Z", fill: "white" }), _jsx("path", { d: "M0 8L8 0L16 8", stroke: Colors.Gray300, strokeWidth: "1", fill: "none" })] }) }), _jsxs("div", { className: "flex flex-col rounded-lg overflow-hidden border -mt-px", style: {
                                                            backgroundColor: 'white',
                                                            borderColor: Colors.Gray300,
                                                            boxShadow: '0px 0px 8px -4px rgba(0,0,0,0.1), 0px 6px 6px 0px rgba(0,0,0,0.06), 0px 2px 6px 0px rgba(0,0,0,0.04), 0px 0px 1px 0px rgba(0,0,0,0.04)',
                                                        }, children: [_jsx("div", { className: "flex items-center gap-2 px-3 py-2", children: _jsx("h3", { className: "flex-1 font-bold leading-[1.2] text-[15px]", style: { color: 'black' }, children: "Conversation History" }) }), conversationsLoading ? (
                                                            /* Loading state */
                                                            _jsx("div", { className: "flex items-center justify-center py-6", children: _jsx("p", { className: "font-sans font-normal leading-normal text-[13px] text-center", style: { color: Colors.Gray600 }, children: "Loading conversations..." }) })) : filteredConversations.length === 0 ? (
                                                            /* Empty state */
                                                            _jsx("div", { className: "flex items-center justify-center py-6", children: _jsx("p", { className: "font-sans font-normal leading-normal text-[13px] text-center", style: { color: Colors.Gray990 }, children: "No conversations found" }) })) : (_jsxs(_Fragment, { children: [_jsx("p", { className: "px-3 pb-2 font-sans font-normal leading-normal text-[13px]", style: { color: Colors.Gray600 }, children: "Click a conversation to restore and continue" }), _jsx("div", { className: "h-px w-full", style: { backgroundColor: Colors.Gray300 } }), _jsxs("p", { className: "px-3 py-1.5 font-sans font-normal leading-normal text-[13px]", style: { color: Colors.Gray600 }, children: [filteredConversations.length, " conversation", filteredConversations.length !== 1 ? 's' : ''] }), _jsx("div", { className: "flex flex-col gap-2 px-3 pb-3 max-h-[350px] overflow-y-auto", children: filteredConversations.map((conv) => (_jsxs("button", { className: "flex flex-col items-start gap-1 p-2 rounded-lg cursor-pointer text-left transition-all border border-transparent", style: {
                                                                                backgroundColor: Colors.Gray100,
                                                                            }, onMouseEnter: (e) => {
                                                                                e.currentTarget.style.backgroundColor = Colors.Gray200;
                                                                                e.currentTarget.style.borderColor = Colors.Gray300;
                                                                            }, onMouseLeave: (e) => {
                                                                                e.currentTarget.style.backgroundColor = Colors.Gray100;
                                                                                e.currentTarget.style.borderColor = 'transparent';
                                                                            }, onClick: () => {
                                                                                if (onSelectConversation) {
                                                                                    onSelectConversation(conv.conversationId, conv.isInMemory);
                                                                                }
                                                                                setShowHistoryPopup(false);
                                                                            }, children: [_jsx("p", { className: "font-sans font-bold leading-normal text-[13px] line-clamp-1 w-full", style: { color: Colors.Gray990 }, children: conv.title }), _jsx("p", { className: "font-sans font-normal leading-normal text-[13px] w-full", style: { color: Colors.Gray800 }, children: formatRelativeTime(conv.lastActiveAt) })] }, conv.conversationId))) })] }))] })] }))] }), showConversationExport && (_jsxs("div", { className: "relative", children: [_jsx("button", { className: "flex w-8 h-8 items-center justify-center rounded-md transition-colors cursor-pointer", style: {
                                                    backgroundColor: activeTooltip === "export-conversation" || exportCopyStatus === "copied"
                                                        ? Colors.PrimaryPurple10
                                                        : "transparent",
                                                }, "aria-label": "Export conversation JSON", onClick: handleExportConversation, onMouseEnter: () => setActiveTooltip("export-conversation"), onMouseLeave: () => setActiveTooltip(null), children: _jsx("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", "aria-hidden": "true", children: _jsx("path", { d: "M12 3V14M12 14L8 10M12 14L16 10M5 18H19", stroke: Colors.PrimaryPurple60, strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" }) }) }), activeTooltip === "export-conversation" && (_jsx("div", { className: "absolute top-full left-1/2 -translate-x-1/2 mt-2 z-50", children: _jsxs("div", { className: "relative", children: [_jsx("svg", { className: "absolute -top-2 left-1/2 -translate-x-1/2", width: "16", height: "8", viewBox: "0 0 16 8", fill: "none", children: _jsx("path", { d: "M8 0L16 8H0L8 0Z", fill: Colors.Gray800 }) }), _jsx("div", { className: "px-2 py-0.5 rounded-lg whitespace-nowrap text-[13px] text-white", style: { backgroundColor: Colors.Gray800 }, children: exportCopyStatus === "copied"
                                                                ? "Copied JSON"
                                                                : exportCopyStatus === "failed"
                                                                    ? "Copy failed"
                                                                    : "Export JSON" })] }) }))] }))] })] }), showHeaderMetaRow && (_jsxs("div", { className: "flex items-center justify-between px-1 pt-[1px] mr-2", children: [hasContextUsage ? (_jsxs("span", { className: "text-[8px] leading-[120%]", style: { color: Colors.Gray500 }, children: ["Context used: ", contextUsagePercent.toFixed(1), "%"] })) : (_jsx("span", {})), shouldShowIndexingStatus && (_jsx("span", { className: "inline-flex items-center gap-[3px] text-[8px] leading-[120%]", style: { color: Colors.Gray500 }, children: indexingEnabled ? (_jsxs(_Fragment, { children: [isSlideIndexingInProgress(slideCountPill) && (_jsx("span", { className: "inline-block w-[8px] h-[8px] border border-current border-t-transparent rounded-full animate-spin shrink-0", "aria-hidden": "true" })), getSlideIndexingStatusText(slideCountPill)] })) : (_jsxs(_Fragment, { children: ["Indexing off (", onOpenSettings ? (_jsx("button", { type: "button", onClick: () => onOpenSettings("indexing"), className: "text-[8px] leading-[120%] font-normal hover:font-bold cursor-pointer", style: { color: Colors.PrimaryPurple60 }, children: "edit" })) : (_jsx("span", { className: "text-[8px] leading-[120%] font-normal", style: { color: Colors.PrimaryPurple60 }, children: "edit" })), ")"] })) }))] }))] })), _jsxs("div", { ref: messagesContainerRef, className: isPlanningMode
                    ? "flex-1 overflow-y-auto flex flex-col gap-3 min-h-0"
                    : "flex-1 px-4 py-4 overflow-y-auto flex flex-col gap-3 scrollbar-thin min-h-0", children: [displayMessages.length === 0 && (_jsx("div", { className: "text-center text-gray-500 text-base italic my-5 font-sans leading-normal", children: isPlanningMode ? "Start a conversation to refine your plan" : "What can I help with?" })), (() => {
                        let userTurnOrdinal = 0;
                        return displayMessages.map((message) => {
                            let onEditMessage;
                            if ("sender" in message && message.sender === "user") {
                                userTurnOrdinal += 1;
                                const targetTurn = userTurnOrdinal;
                                if (onRewriteConversationFromUserTurn) {
                                    onEditMessage = async (payload) => {
                                        await handleEditFromUserTurn(targetTurn, payload.text, payload.userImageAttachments, payload.mentions, payload.html);
                                    };
                                }
                            }
                            return (_jsx(ChatMessageItem, { message: message, onAddToPrompt: handleAddToPrompt, onEditMessage: onEditMessage, fetchMentionItems: fetchMentionItems, onRequestMentionUpload: handleMentionUploadRequest, uploadingFiles: uploadingFiles }, message.id));
                        });
                    })(), backendError && !backendError.toLowerCase().includes('cancelled') && (_jsxs("div", { className: "text-red-600 text-sm px-4 py-2 bg-red-50 rounded", children: ["Error: ", backendError] })), isLoading && (_jsxs("div", { className: "flex items-center justify-between gap-2", children: [_jsxs("div", { className: "flex items-center gap-1", children: [_jsx("span", { className: "text-base text-brand-600 font-sans", children: "Processing..." }), _jsx("div", { className: "inline-block w-[22px] h-[22px]", children: _jsx(Lottie, { animationData: dotsAnimation, loop: true, autoplay: true }) })] }), _jsxs("button", { onClick: stopExecution, className: "flex items-center gap-1.5 px-1.5 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer", title: "Stop processing (Ctrl+X)", "aria-label": "Stop processing", children: [_jsx(StopIcon, { size: 16, className: "text-gray-500" }), _jsx("span", { children: "Stop" })] })] })), _jsx("div", { ref: messagesEndRef })] }), isPlanningMode ? (_jsx("div", { children: _jsx(TextInput, { value: inputValue, onChange: setInputValue, onSubmit: handleSendMessage, placeholder: "Ask to modify your plan...", disabled: isLoading }) })) : (_jsxs("div", { className: "px-2 py-4 bg-white flex flex-col gap-2", children: [uploadingFiles.length > 0 && (_jsx(FileUploadProgress, { files: uploadingFiles, onRemoveFile: handleRemoveUploadingFile })), _jsxs("div", { className: `min-h-20 px-2 py-2 bg-gray-100 rounded-[10px] outline-1 -outline-offset-1 flex flex-col justify-center items-start gap-2 ${isDragOverUploadZone
                            ? 'outline-primary-purple-40'
                            : isTextareaFocused
                                ? 'outline-primary-purple-20'
                                : 'outline-gray-300'}`, onDragEnter: handleUploadZoneDragEnter, onDragOver: handleUploadZoneDragOver, onDragLeave: handleUploadZoneDragLeave, onDrop: handleUploadZoneDrop, onPasteCapture: handleComposerPasteCapture, style: {
                            boxShadow: isTextareaFocused
                                ? '0px 0px 1px 0px rgba(0,0,0,0.04), 0px 0px 2px 0px rgba(0,0,0,0.06), 0px 4px 4px 0px rgba(0,0,0,0.06), 0px 0px 6px -4px rgba(0,0,0,0.10)'
                                : 'none',
                            backgroundColor: isDragOverUploadZone ? Colors.PrimaryPurple05 : undefined,
                        }, children: [composerImages.length > 0 && (_jsx(ChatImageAttachments, { images: composerImages, onRemoveImage: (imageId) => {
                                    void removeComposerImage(imageId);
                                } })), imageAttachmentErrors.length > 0 && (_jsxs("div", { className: "w-full rounded-md border px-2 py-1", style: { borderColor: Colors.Red40, backgroundColor: Colors.Red10 }, children: [_jsxs("div", { className: "mb-1 flex items-center justify-between gap-2", children: [_jsx("span", { className: "text-[12px] font-bold leading-normal", style: { color: Colors.Red70 }, children: "Some images were skipped" }), _jsx("button", { type: "button", className: "border-0 bg-transparent p-0 text-[11px] font-bold cursor-pointer", style: { color: Colors.Red70 }, onClick: () => setImageAttachmentErrors([]), children: "Clear" })] }), _jsx("div", { className: "flex flex-col gap-[2px]", children: imageAttachmentErrors.map((message, index) => (_jsx("p", { className: "text-[11px] leading-normal", style: { color: Colors.Red70 }, children: message }, `${message}-${index}`))) })] })), _jsx("div", { className: "self-stretch pl-1 flex justify-start items-center gap-2", children: _jsx("div", { className: "flex-1 py-0.5 relative flex justify-start items-center", children: _jsx(ChatInput, { ref: chatInputRef, value: inputValue, onChange: setInputValue, onKeyDown: handleKeyDown, onFocus: () => setIsTextareaFocused(true), onBlur: () => setIsTextareaFocused(false), placeholder: "What do you need help with?", disabled: isLoading || !conversationId, fetchMentionItems: fetchMentionItems, onUploadClick: () => handleUploadFileClick("mention", chatInputRef.current), uploadingFiles: uploadingFiles }) }) }), _jsxs("div", { className: "flex items-end gap-2 self-stretch w-full", children: [_jsx("input", { ref: uploadInputRef, type: "file", multiple: true, accept: ACCEPTED_UPLOAD_FILE_TYPES, className: "hidden", onChange: handleFileInputChange }), _jsxs("div", { className: "flex items-end gap-2 flex-1", children: [_jsxs("button", { className: "inline-flex items-center justify-center gap-1 px-2.5 py-[2.5px] bg-transparent border border-gray-300 rounded-md cursor-pointer hover:bg-gray-100 transition-colors", onClick: () => handleUploadFileClick("composer"), "aria-label": "Upload file", children: [_jsx(UploadIcon, { size: 16, color: "#374151" }), _jsx("span", { className: "text-[13px] font-bold text-gray-800 leading-[150%]", children: "Upload File" })] }), showConversationExport && (_jsxs("button", { className: "inline-flex items-center justify-center gap-1 px-2.5 py-[2.5px] bg-transparent border border-gray-300 rounded-md cursor-pointer hover:bg-gray-100 transition-colors", onClick: handleExportConversation, "aria-label": "Export conversation JSON", children: [_jsx("svg", { width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", "aria-hidden": "true", children: _jsx("path", { d: "M12 3V14M12 14L8 10M12 14L16 10M5 18H19", stroke: "#374151", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" }) }), _jsx("span", { className: "text-[13px] font-bold text-gray-800 leading-[150%]", children: exportCopyStatus === "copied"
                                                            ? "Copied JSON"
                                                            : exportCopyStatus === "failed"
                                                                ? "Copy failed"
                                                                : "Export JSON" })] }))] }), _jsx(AIPrimaryButton, { onClick: handleSendMessage, disabled: (inputValue.trim() === "" && composerImages.length === 0) || isLoading || !conversationId, icon: _jsx(SendIcon, { size: 24, className: "text-white" }) })] })] })] })), _jsx(FileConflictModal, { isOpen: !!pendingConflictSession, items: (pendingConflictSession?.files || [])
                    .filter((item) => item.requires_decision)
                    .map((item) => ({
                    item_id: item.item_id,
                    filename: item.filename,
                    file_size: item.file_size,
                    conflict_type: item.conflict_type,
                    existing_matches: item.existing_matches,
                })), isSubmitting: isCommittingConflicts, onCancel: () => {
                    if (isCommittingConflicts)
                        return;
                    mentionUploadIntentRef.current = false;
                    mentionUploadTargetRef.current = null;
                    setPendingConflictSession(null);
                }, onApply: async (decisions) => {
                    if (!pendingConflictSession)
                        return;
                    try {
                        setIsCommittingConflicts(true);
                        const uploadedEntries = await commitUploadSession(pendingConflictSession, decisions);
                        if (mentionUploadIntentRef.current && uploadedEntries.length > 0) {
                            (mentionUploadTargetRef.current || chatInputRef.current)?.insertUploadedMentions(uploadedEntries);
                        }
                        setPendingConflictSession(null);
                    }
                    catch (error) {
                        // Keep modal open so user can retry.
                    }
                    finally {
                        mentionUploadIntentRef.current = false;
                        mentionUploadTargetRef.current = null;
                        setIsCommittingConflicts(false);
                    }
                } })] }));
};
export default ChatPanel;
